import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Rocket, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { reportError } from "@/lib/error-reporting";
import { toast } from "@/hooks/use-toast";
import type { Persona } from "@/lib/persona-content";

// ─── Config ──────────────────────────────────────────────────────────────────

const TEAM_SIZE_OPTIONS = ["2-10", "11-50", "51-200", "200+"];
const ROLE_OPTIONS = ["L&D", "Manager", "HR", "VP/Executive", "Other"];
const PRACTICE_SIZE_OPTIONS = ["1-5 clients", "6-15 clients", "16-30 clients", "30+ clients"];

const PERSONA_CONFIG = {
  team: {
    title: "Request a Team Pilot",
    description: "Tell us about your team and we'll set up a personalized pilot.",
  },
  coach: {
    title: "Apply for Coach Access",
    description: "Tell us about your practice and we'll get you set up.",
  },
} as const;

// ─── Schemas ─────────────────────────────────────────────────────────────────

const baseSchema = z.object({
  email: z.string().trim().email("Please enter a valid email address").max(255),
  first_name: z.string().trim().min(1, "First name is required").max(100),
});

const teamSchema = baseSchema.extend({
  company: z.string().trim().min(1, "Company name is required").max(255),
  team_size: z.string().min(1, "Please select a team size"),
  role: z.string().min(1, "Please select your role"),
});

const coachSchema = baseSchema.extend({
  certification: z.string().trim().max(255).optional(),
  practice_size: z.string().min(1, "Please select your practice size"),
  referral_source: z.string().trim().max(100).optional(),
});

type TeamFormData = z.infer<typeof teamSchema>;
type CoachFormData = z.infer<typeof coachSchema>;

// ─── Component ───────────────────────────────────────────────────────────────

interface PilotRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona: "team" | "coach";
}

export function PilotRequestModal({ open, onOpenChange, persona }: PilotRequestModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  const config = PERSONA_CONFIG[persona];

  const teamForm = useForm<TeamFormData>({
    resolver: zodResolver(teamSchema),
    defaultValues: { email: "", first_name: "", company: "", team_size: "", role: "" },
  });

  const coachForm = useForm<CoachFormData>({
    resolver: zodResolver(coachSchema),
    defaultValues: { email: "", first_name: "", certification: "", practice_size: "", referral_source: "" },
  });

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset state when closing
      setTimeout(() => {
        setSuccess(false);
        setSubmittedName("");
        teamForm.reset();
        coachForm.reset();
      }, 200);
    }
    onOpenChange(isOpen);
  };

  const submitRequest = async (data: TeamFormData | CoachFormData) => {
    setIsSubmitting(true);
    try {
      const insertData: Record<string, string> = {
        email: data.email.toLowerCase(),
        first_name: data.first_name,
        persona_type: persona,
      };

      if (persona === "team") {
        const teamData = data as TeamFormData;
        insertData.company = teamData.company;
        insertData.team_size = teamData.team_size;
        insertData.role = teamData.role;
      } else {
        const coachData = data as CoachFormData;
        if (coachData.certification) insertData.certification = coachData.certification;
        insertData.practice_size = coachData.practice_size;
        if (coachData.referral_source) insertData.referral_source = coachData.referral_source;
      }

      const { error } = await supabase
        .from("pilot_requests" as never)
        .insert(insertData as never);

      if (error) {
        // Handle unique constraint violation (duplicate pending request)
        if (error.code === "23505") {
          setSubmittedName(data.first_name);
          setSuccess(true);
          return;
        }
        throw error;
      }

      setSubmittedName(data.first_name);
      setSuccess(true);

      // Fire-and-forget notification email
      supabase.functions.invoke("send-pilot-request-notification", {
        body: {
          email: data.email.toLowerCase(),
          firstName: data.first_name,
          personaType: persona,
          ...insertData,
        },
      }).catch(() => {
        // Notification failure is non-critical
      });

    } catch (error) {
      reportError(error, "pilot-request", { action: "submit", persona });
      toast({
        title: "Something went wrong",
        description: "Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Success View ────────────────────────────────────────────────────────

  if (success) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center py-6 space-y-4">
            <div className="p-3 rounded-full bg-gradient-to-br from-primary/20 to-accent/20">
              <CheckCircle2 className="w-8 h-8 text-accent" />
            </div>
            <h3 className="text-xl font-semibold">
              Thanks, {submittedName}!
            </h3>
            <p className="text-muted-foreground">
              We'll be in touch within 48 hours to get you set up.
            </p>
            <Button onClick={() => handleClose(false)} className="mt-2">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Form View ───────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20">
              <Rocket className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle>{config.title}</DialogTitle>
              <DialogDescription className="text-sm">
                {config.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {persona === "team" ? (
          <Form {...teamForm}>
            <form onSubmit={teamForm.handleSubmit(submitRequest)} className="space-y-4">
              <FormField
                control={teamForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Work Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@company.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={teamForm.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your first name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={teamForm.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input placeholder="Your company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={teamForm.control}
                name="team_size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Team Size</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select team size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TEAM_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={teamForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Role</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ROLE_OPTIONS.map((role) => (
                          <SelectItem key={role} value={role}>{role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Request a Team Pilot"
                )}
              </Button>
            </form>
          </Form>
        ) : (
          <Form {...coachForm}>
            <form onSubmit={coachForm.handleSubmit(submitRequest)} className="space-y-4">
              <FormField
                control={coachForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={coachForm.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your first name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={coachForm.control}
                name="certification"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Certification (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. ICF PCC, CTI CPCC" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={coachForm.control}
                name="practice_size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Practice Size</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select practice size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PRACTICE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={coachForm.control}
                name="referral_source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>How did you hear about us? (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. LinkedIn, colleague" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Apply for Coach Access"
                )}
              </Button>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
